import { Contact } from '@api/generated/graphql';
import {
  Stack,
  Icon,
  IconNameType,
  BodyText,
} from '@servicetitan/design-system';
import SearchResultBadge from '../SearchResultBadge';
import ContactMethodLink from '@components/ContactMethodLink/ContactMethodLink';
import { SyntheticEvent } from 'react';

interface CustomerSearchResultInfoItemProps {
  iconName: IconNameType;
  content: string | Contact;
  badgeCount?: number;
  className?: string;
  renderBadge?: React.ReactNode;
}

/**
 * Renders a single customer information item with an icon and text and optional badge.
 *
 * @param iconName {String} - icon name to be displayed at the center before the information
 * @param text {String} - text to be displayed (e.g. name, address, phone number, email)
 * @param width {String} - optional width of the container
 * @param badgeCount {Number} - optional badge count to be displayed at the right side of the text with a plus sign in front
 * @returns {JSX.Element} - rendered customer information component
 */
const CustomerSearchResultInfoItem = ({
  iconName,
  content,
  badgeCount,
  className,
  renderBadge,
}: CustomerSearchResultInfoItemProps) => {
  const isContact = typeof content !== 'string';

  return (
    <div
      className={className}
      // since the parent element has an onClick event, we need to stop the propagation of any clicks within this element
      onClick={(e: SyntheticEvent) => isContact && e.stopPropagation()}
    >
      <Stack alignItems='center' spacing={1}>
        <Icon className='c-neutral-100' name={iconName} size='16px' />
        <Stack.Item>
          {isContact ? (
            <ContactMethodLink contact={content} />
          ) : (
            <BodyText size='small'>{content}</BodyText>
          )}
        </Stack.Item>
        {!!badgeCount && <SearchResultBadge count={badgeCount} />}
        {!badgeCount && renderBadge}
      </Stack>
    </div>
  );
};

export default CustomerSearchResultInfoItem;
